/**
 * Browser downloads, in one place.
 *
 * The createObjectURL → anchor → click → revoke dance used to live inline in
 * GuestsTab and again in the host export. One copy means a future fix — a
 * Safari blob quirk, `rel=noopener`, a try/finally around the revoke — lands
 * everywhere at once.
 *
 * Side-effecting: needs `document` and `URL.createObjectURL`, so it is
 * exercised by hand rather than under node vitest.
 */
export function downloadBlob(filename: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  try {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
  } finally {
    URL.revokeObjectURL(url);
  }
}

export function downloadTextFile(
  filename: string,
  content: string,
  mimeType = 'text/csv;charset=utf-8',
): void {
  downloadBlob(filename, new Blob([content], { type: mimeType }));
}

/**
 * A host-typed name, made safe to use inside a download filename.
 *
 * `link.download = 'seating-chart-Sat/Sun dinner.csv'` is read by Chrome as a
 * path, and it will try to make a `Sat` folder. Reserved characters and
 * whitespace become a dash, runs collapse, and an empty result falls back to
 * the caller's default.
 */
export function safeFilename(name: string | null | undefined, fallback: string): string {
  const cleaned = String(name ?? '')
    .replace(/[\\/:*?"<>|\s]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^[.-]+|[.-]+$/g, '');
  return cleaned || fallback;
}
